/**
 * Locale-aware date formatting utilities for JPH Portal.
 * Reads the current i18n language and formats dates accordingly.
 *
 * English:  12 Sep 2025
 * Japanese: 2025年9月12日
 * Chinese:  2025年9月12日
 */

import i18n from '@/i18n';

function getLocale(lang?: string): string {
  const l = lang || i18n.language || 'en';
  if (l === 'ja') return 'ja-JP';
  if (l === 'zh') return 'zh-CN';
  return 'en-NZ';
}

/**
 * Format a date string as a short date: "12 Sep 2025" / "2025年9月12日"
 */
export function formatDate(dateStr: string | null | undefined, lang?: string): string {
  if (!dateStr) return '—';
  try {
    const locale = getLocale(lang);
    if (locale === 'ja-JP' || locale === 'zh-CN') {
      return new Date(dateStr).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    return new Date(dateStr).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format a date string as a short date without year: "12 Sep" / "9月12日"
 */
export function formatShortDate(dateStr: string | null | undefined, lang?: string): string {
  if (!dateStr) return '—';
  try {
    const locale = getLocale(lang);
    if (locale === 'ja-JP' || locale === 'zh-CN') {
      return new Date(dateStr).toLocaleDateString(locale, {
        month: 'long',
        day: 'numeric',
      });
    }
    return new Date(dateStr).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format a date string with time: "12 Sep, 14:30" / "9月12日 14:30"
 */
export function formatDateTime(dateStr: string | null | undefined, lang?: string): string {
  if (!dateStr) return '—';
  try {
    const locale = getLocale(lang);
    if (locale === 'ja-JP' || locale === 'zh-CN') {
      return new Date(dateStr).toLocaleDateString(locale, {
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return new Date(dateStr).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format a time string: "14:30"
 */
export function formatTime(dateStr: string | null | undefined, lang?: string): string {
  if (!dateStr) return '—';
  try {
    const locale = getLocale(lang);
    return new Date(dateStr).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

/**
 * Relative time: "just now", "5 min ago", "2 hrs ago", "3 days ago", "2 weeks ago"
 * Returns a key-value pair for use with t() interpolation.
 */
export function timeAgoKey(iso: string): { key: string; params: Record<string, number> } {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return { key: 'notifications.justNow', params: {} };
  if (mins < 60) return { key: 'notifications.minAgo', params: { n: mins } };
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return { key: hrs === 1 ? 'notifications.hrAgo' : 'notifications.hrsAgo', params: { n: hrs } };
  const days = Math.floor(hrs / 24);
  if (days < 7) return { key: days === 1 ? 'notifications.dayAgo' : 'notifications.daysAgo', params: { n: days } };
  const weeks = Math.floor(days / 7);
  return { key: weeks === 1 ? 'notifications.weekAgo' : 'notifications.weeksAgo', params: { n: weeks } };
}

/**
 * Translate a notification title based on notification_type.
 * Keeps user-entered names/titles as-is; only translates the system-generated prefix.
 *
 * @param type  - notification_type from DB (e.g. "daily_log_created")
 * @param title - raw title stored in DB (may contain user-entered names)
 * @param t     - i18next t() function
 * @returns translated title string
 */
export function translateNotificationTitle(
  type: string,
  title: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const typeKey = `notif.type.${type}`;

  switch (type) {
    case 'daily_log_created': {
      // "New Daily Log: {name}" — extract name after colon
      const colonIdx = title.indexOf(':');
      const name = colonIdx >= 0 ? title.slice(colonIdx + 1).trim() : title;
      const prefix = t(typeKey, { defaultValue: '' });
      return prefix ? `${prefix}: ${name}` : title;
    }
    case 'instruction_created': {
      const colonIdx = title.indexOf(':');
      const name = colonIdx >= 0 ? title.slice(colonIdx + 1).trim() : title;
      const prefix = t(typeKey, { defaultValue: '' });
      return prefix ? `${prefix}: ${name}` : title;
    }
    case 'change_order_approved': {
      // "Client approved {title} of change order" — extract the title part
      // Pattern: "Client approved X of change order" or "Client approved X"
      const match = title.match(/Client approved (.+?) of change order/i)
        || title.match(/Client approved (.+)/i);
      const orderTitle = match ? match[1].trim() : title;
      const translated = t(typeKey, { title: orderTitle, defaultValue: '' });
      return translated || title;
    }
    case 'schedule_recalculated': {
      const translated = t(typeKey, { defaultValue: '' });
      return translated || title;
    }
    case 'critical_path_delayed': {
      // "Critical path item delayed: {title}"
      const colonIdx = title.indexOf(':');
      const itemTitle = colonIdx >= 0 ? title.slice(colonIdx + 1).trim() : title;
      const translated = t(typeKey, { title: itemTitle, defaultValue: '' });
      return translated || title;
    }
    default: {
      // For other types, check if we have a translation key
      const translated = t(typeKey, { defaultValue: '' });
      if (translated) {
        // If the title contains a colon, keep the user-entered part
        const colonIdx = title.indexOf(':');
        if (colonIdx >= 0) {
          const name = title.slice(colonIdx + 1).trim();
          return `${translated}: ${name}`;
        }
        return translated;
      }
      return title;
    }
  }
}

/**
 * Translate a notification body based on notification_type.
 * Keeps user-entered content as-is; only translates system-generated bodies.
 */
export function translateNotificationBody(
  type: string,
  body: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
  lang?: string
): string {
  switch (type) {
    case 'schedule_recalculated': {
      const translated = t('notif.body.schedule_recalculated', { defaultValue: '' });
      return translated || body;
    }
    case 'critical_path_delayed': {
      // "now delayed. Expected completion {date}."
      // Extract date from body if present
      const dateMatch = body.match(/Expected completion (.+?)\./i);
      if (dateMatch) {
        const rawDate = dateMatch[1].trim();
        // Try to parse and reformat the date
        const parsed = new Date(rawDate);
        const formattedDate = !Number.isNaN(parsed.getTime())
          ? formatDate(parsed.toISOString(), lang)
          : rawDate;
        const translated = t('notif.body.critical_path_delayed', { date: formattedDate, defaultValue: '' });
        return translated || body;
      }
      const translated = t('notif.body.critical_path_delayed', { date: '', defaultValue: '' });
      return translated || body;
    }
    case 'change_order_approved': {
      const translated = t('notif.body.change_order_approved', { defaultValue: '' });
      return translated || body;
    }
    default:
      return body;
  }
}